// src/components/AuftragSelect.js
// Auftragsauswahl mit Suchfeld (gemeinsam für NewEntryForm und EditEntryForm)  #P360#
import React, { useMemo, useState } from 'react';

const sortByNr = (a, b) =>
  String(a.AufNr).length - String(b.AufNr).length || String(a.AufNr).localeCompare(String(b.AufNr));

// Kleinschreibung und Akzente entfernen, damit "turen" auch "Türen" findet
const normalize = (text) =>
  String(text).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const filterAuftraege = (auftraege, search) => {
  const terms = normalize(search).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return auftraege;
  return auftraege.filter(a => {
    const text = normalize(`${a.AufNr} ${a.AufBez}`);
    return terms.every(term => text.includes(term));
  });
};

const AuftragSelect = ({ id, name, value, onChange, auftraege, auftragUsage = {}, style }) => {
  const [search, setSearch] = useState('');

  const activeAuftraege = useMemo(() => auftraege.filter(a => !a.AufAbschluss), [auftraege]);

  const { frequent, rest } = useMemo(() => {
    const frequent = activeAuftraege
      .filter(a => auftragUsage[a.AufID] > 0)
      .sort((a, b) => (auftragUsage[b.AufID] || 0) - (auftragUsage[a.AufID] || 0))
      .slice(0, 12)
      .sort(sortByNr);
    const frequentIds = new Set(frequent.map(a => a.AufID));
    const rest = activeAuftraege
      .filter(a => !frequentIds.has(a.AufID))
      .sort(sortByNr);
    return { frequent, rest };
  }, [activeAuftraege, auftragUsage]);

  const isSearching = search.trim() !== '';
  const matches = useMemo(
    () => (isSearching ? filterAuftraege(activeAuftraege, search).sort(sortByNr) : []),
    [isSearching, activeAuftraege, search]
  );

  const selectValue = (newValue) => {
    onChange({ target: { name, value: newValue } });
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearch(term);
    if (term.trim() === '') return;

    const found = filterAuftraege(activeAuftraege, term);
    if (found.length === 1) {
      // Genau ein Treffer: direkt auswählen
      selectValue(String(found[0].AufID));
    } else if (value && !found.some(a => String(a.AufID) === String(value))) {
      // Bisherige Auswahl ist nicht mehr in der Liste: zurücksetzen statt unsichtbar behalten
      selectValue('');
    }
  };

  const clearSearch = () => setSearch('');

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape' && search) {
      e.preventDefault();
      clearSearch();
    }
    // Enter soll nicht das Formular absenden
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const renderOption = (auftrag) => (
    <option key={auftrag.AufID} value={auftrag.AufID}>
      {auftrag.AufNr} - {auftrag.AufBez}
    </option>
  );

  return (
    <>
      <div style={{ position: 'relative' }}>
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#888"
          strokeWidth="2.5"
          strokeLinecap="round"
          style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', marginTop: '2px' }}
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="16.5" y1="16.5" x2="21" y2="21" />
        </svg>
        <input
          type="text"
          id={`${id}-search`}
          value={search}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          placeholder="Auftrag suchen…"
          aria-label="Auftrag suchen"
          aria-controls={id}
          autoComplete="off"
          style={{ ...style, paddingLeft: '34px', paddingRight: search ? '36px' : style?.padding }}
        />
        {search && (
          <button
            type="button"
            onClick={clearSearch}
            aria-label="Suche löschen"
            title="Suche löschen"
            style={{
              position: 'absolute',
              right: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              marginTop: '2px',
              width: '30px',
              height: '30px',
              padding: 0,
              border: 'none',
              background: 'transparent',
              color: '#888',
              fontSize: '18px',
              lineHeight: '30px',
              cursor: 'pointer'
            }}
          >
            ✕
          </button>
        )}
      </div>

      {isSearching && (
        <div aria-live="polite" style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 2px' }}>
          {matches.length > 0
            ? `${matches.length} von ${activeAuftraege.length} Aufträgen`
            : 'Keine Aufträge gefunden'}
        </div>
      )}

      <select
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        required
        style={{ ...style, marginTop: '6px' }}
      >
        <option value="">Bitte wählen...</option>
        {isSearching ? (
          matches.map(renderOption)
        ) : (
          <>
            {frequent.length > 0 && (
              <>
                <option disabled>── Häufig verwendet ──</option>
                {frequent.map(renderOption)}
              </>
            )}
            {rest.length > 0 && (
              <>
                <option disabled>── Alle Aufträge ──</option>
                {rest.map(renderOption)}
              </>
            )}
          </>
        )}
      </select>
    </>
  );
};

export default AuftragSelect;
